/**
 * HookConfigPanel Component
 * Right sidebar showing hook information (read-only)
 * Configuration parameters are managed in Agent Config section
 */

import React from 'react';
import type { HookConfig } from '../../types/hooks';
import type { HooksMessages } from '@/i18n/messages';

interface HookConfigPanelProps {
  hook: HookConfig | null;
  translations: HooksMessages;
}

export const HookConfigPanel: React.FC<HookConfigPanelProps> = ({
  hook,
  translations: t,
}) => {
  // Get i18n translations for hook display_name and description
  const hookDef = hook ? t.hookDefinitions[hook.hook_name as keyof typeof t.hookDefinitions] : null;
  const displayName = hookDef?.displayName || hook?.display_name;
  const description = hookDef?.description || hook?.description;

  if (!hook) {
    return (
      <div className="border rounded-lg p-8 bg-gray-50 text-center sticky top-4">
        <svg
          className="w-12 h-12 text-gray-300 mx-auto mb-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122"
          />
        </svg>
        <p className="text-gray-500">{t.selectHook}</p>
      </div>
    );
  }

  return (
    <div className="border rounded-lg p-4 bg-white sticky top-4 max-h-[calc(100vh-8rem)] overflow-y-auto">
      <h3 className="text-lg font-semibold mb-4">{displayName}</h3>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1">{t.description}</label>
          <p className="text-sm text-gray-600">{description || t.noDescription}</p>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">{t.stage}</label>
          <span className="inline-block px-2 py-1 text-xs font-medium bg-gray-100 rounded">
            {t.stages[hook.stage as keyof typeof t.stages] || hook.stage}
          </span>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">{t.category}</label>
          <span className="inline-block px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded">
            {hook.category}
          </span>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">{t.scope}</label>
          <span className="inline-block px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded">
            {hook.scope}
          </span>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">{t.sortOrder}</label>
          <input
            type="number"
            value={hook.sort_order}
            disabled
            className="w-full px-3 py-2 border rounded bg-gray-50"
          />
          <p className="text-xs text-gray-500 mt-1">
            {t.dragToReorder}
          </p>
        </div>
      </div>

      {/* Info box pointing to Agent Config for parameter editing */}
      <div className="mt-6 pt-4 border-t">
        <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <svg
              className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p className="text-sm text-blue-700">
              {t.configInAgentConfig || 'Hook parameters are configured in the Agent Config section under the corresponding hook settings.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
